// ── 계약 모달 — 고객정보·배송정보·담당자 섹션 ───────────────────────────────
import React, { useState } from 'react';
import CourierInput from '../../../components/CourierInput';
import { FormField, Req } from './utils';
import { isFieldVisible, isRequired } from './contractFieldMeta';
import {
  sectionCard, sectionTitleBar, sectionDot,
  twoColStyle, inputStyle,
} from './modalStyles';


// 설정이 없으면(cfg=null) 지금 화면 그대로 둔다 — 무회귀.
//   vis: 이 칸을 보일지 / lab: 필수면 빨간 * 를 붙인 라벨
function makeHelpers(cfg, sectionId, isNew, defaultReq) {
  const vis = (id) => (cfg ? isFieldVisible(cfg, sectionId, id, { isNew }) : true);
  const lab = (id, name) => {
    const req = cfg ? isRequired(cfg, id) : defaultReq.includes(id);
    return req ? <Req label={name} /> : name;
  };
  return { vis, lab };
}

// ── 고객 정보 섹션 ────────────────────────────────────────────────────────────
export function CustomerSection({ form, errors, setField, inp, isMobile, cfg = null, isNew = false }) {
  const { vis, lab } = makeHelpers(cfg, 'customer', isNew, ['company_name', 'phone', 'mobile_phone', 'address']);
  return (
    <div style={sectionCard}>
      <div style={sectionTitleBar('#0ea5e9')}>
        <span style={sectionDot('#0ea5e9')} />
        고객 정보
      </div>
      {/* 상호 (전체 너비) */}
      {vis('company_name') && <FormField label={lab('company_name', '상호')}>
        <input style={inp('company_name')} value={form.company_name}
          onChange={(e) => setField('company_name', e.target.value)} placeholder="상호명" />
      </FormField>}
      {/* 모바일에서 1열로 전환 — 전화번호 길이 잘림 방지 */}
      {(vis('phone') || vis('mobile_phone')) && <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
        {vis('phone') && <FormField label={lab('phone', '전화번호')}>
          <input style={inp('phone')} value={form.phone}
            onChange={(e) => setField('phone', e.target.value)} placeholder="02-XXXX-XXXX" />
        </FormField>}
        {vis('mobile_phone') && <FormField label={lab('mobile_phone', '휴대폰')}>
          <input style={inp('mobile_phone')} value={form.mobile_phone}
            onChange={(e) => setField('mobile_phone', e.target.value)} placeholder="010-XXXX-XXXX" />
        </FormField>}
      </div>}
      {/* 주소 */}
      {vis('address') && <FormField label={lab('address', '주소')}>
        <input
          style={inp('address')}
          value={form.address}
          onChange={(e) => setField('address', e.target.value)}
          placeholder="주소 입력"
        />
      </FormField>}
      {/* 비고 (선택 — contracts 전용 메모, 콜현황과 무관) */}
      {vis('memo') && <FormField label={lab('memo', '비고')}>
        <textarea
          style={{ ...inp('memo'), resize: 'vertical', minHeight: 56, lineHeight: 1.5 }}
          value={form.memo}
          onChange={(e) => setField('memo', e.target.value)}
          placeholder="계약 관련 메모 (선택)"
          rows={2}
        />
      </FormField>}
    </div>
  );
}

// ── 배송 정보 섹션 (수정 모드에서만 표시) ─────────────────────────────────────
// 직접수령(2026-10-30): 머리 줄 체크 → 맨 처음 배송 줄(목록 맨 아래 — [+ 추가]는 위에 새 줄을 넣는다)에 is_pickup.
//   그 줄은 칸 대신 "직접수령 · 배송비 없음" — 송장번호가 없으니 배송비 0원(택배비 = 송장번호 있는 줄만, docs/db.md).
//   나중에 [+ 추가]한 줄은 보통 배송(송장번호 칸·배송비). 송장번호가 적힌 줄은 체크를 막는다(실수로 지워지지 않게).
export function DeliverySection({ form, setForm, formDesignCourier, cfg = null, isNew = false }) {
  const { vis } = makeHelpers(cfg, 'delivery', isNew, []);
  const showCourier = vis('courier');
  const showTracking = vis('tracking_number');
  const [pickupHint, setPickupHint] = useState(false);
  const items = form.deliveryItems || [];
  const isPickup = items.some((i) => i.is_pickup);
  const pickupBlocked = !isPickup && Boolean(items[items.length - 1]?.tracking_number);
  const togglePickup = () => {
    if (pickupBlocked) { setPickupHint(true); return; }
    setPickupHint(false);
    setForm((p) => {
      const list = [...(p.deliveryItems || [])];
      const at = list.findIndex((i) => i.is_pickup);
      if (at >= 0) list[at] = { courier: formDesignCourier || '', tracking_number: '', is_return: false };
      else if (list.length) list[list.length - 1] = { courier: '', tracking_number: '', is_return: false, is_pickup: true };
      else list.push({ courier: '', tracking_number: '', is_return: false, is_pickup: true });
      return { ...p, deliveryItems: list };
    });
  };
  // 밑줄은 머리 줄 전체에 — 제목에만 두면 버튼 옆에서 글자 길이만큼만 그어졌다(다른 섹션처럼 끝까지)
  const titleBar = sectionTitleBar('#0369a1');
  return (
    <div style={sectionCard}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingBottom: titleBar.paddingBottom, borderBottom: titleBar.borderBottom }}>
        <div style={{ ...titleBar, paddingBottom: 0, borderBottom: 'none' }}>
          <span style={sectionDot('#0369a1')} />
          배송 정보
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: isPickup ? '#0369a1' : 'var(--text-sub)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={isPickup} onChange={togglePickup} style={{ margin: 0, width: 15, height: 15, accentColor: '#0369a1', cursor: 'pointer' }} />
            직접수령
          </label>
          {/* 배송 세트 추가 버튼 */}
          <button
            type="button"
            onClick={() => setForm((p) => ({ ...p, deliveryItems: [{ courier: formDesignCourier || '', tracking_number: '' }, ...p.deliveryItems] }))}
            style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.3)', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}
          >
            + 추가
          </button>
        </div>
      </div>
      {pickupHint && pickupBlocked && (
        <div style={{ fontSize: 12, color: '#dc2626' }}>송장번호가 적힌 배송은 직접수령으로 바꿀 수 없어요. 송장번호를 먼저 지워 주세요.</div>
      )}
      {items.map((item, idx) => (
        <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
          {item.is_pickup && (
            <div style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 700, color: '#0369a1', background: 'rgba(14,165,233,0.08)', border: '1.5px dashed rgba(14,165,233,0.35)', borderRadius: 7, padding: '7px 10px' }}>
              직접수령 · 배송비 없음
            </div>
          )}
          {!item.is_pickup && showCourier && <CourierInput
            value={item.courier || ''}
            onChange={(v) => setForm((p) => {
              const items = [...p.deliveryItems];
              items[idx] = { ...items[idx], courier: v };
              return { ...p, deliveryItems: items };
            })}
            placeholder="택배사"
            style={{ flex: '0 0 140px' }}
          />}
          {!item.is_pickup && showTracking && <input
            type="text"
            placeholder="송장번호"
            value={item.tracking_number || ''}
            onChange={(e) => setForm((p) => {
              const items = [...p.deliveryItems];
              items[idx] = { ...items[idx], tracking_number: e.target.value };
              return { ...p, deliveryItems: items };
            })}
            style={{ ...inputStyle, flex: 1, fontSize: 12, border: '1.5px solid rgba(14,165,233,0.35)', borderRadius: 7, background: 'var(--bg-card)' }}
          />}
          {/* 회수 버튼 — 택배사+송장번호 모두 입력된 경우에만 표시
              활성화 시 정산서에서 택배비 ×2 적용 (발송비 + 회수비) */}
          {item.courier && item.tracking_number && (
            <button
              type="button"
              title={item.is_return ? '회수 취소 (택배비 ×2 해제)' : '회수 등록 (택배비 ×2 적용)'}
              onClick={() => setForm((p) => {
                const items = [...p.deliveryItems];
                items[idx] = { ...items[idx], is_return: !items[idx].is_return };
                return { ...p, deliveryItems: items };
              })}
              style={{
                fontSize: 11, fontWeight: 700, flexShrink: 0, cursor: 'pointer', borderRadius: 6, padding: '3px 10px',
                border: item.is_return ? '1px solid #dc2626' : '1px solid var(--border)',
                background: item.is_return ? 'rgba(220,38,38,0.12)' : 'var(--bg-card)',
                color: item.is_return ? '#dc2626' : 'var(--text-sub)',
              }}
            >
              회수{item.is_return ? ' ✓' : ''}
            </button>
          )}
          {/* 첫 번째 항목은 삭제 불가 (최소 1개 유지), 추가 항목은 삭제 가능 */}
          {(form.deliveryItems || []).length > 1 && (
            <button
              type="button"
              onClick={() => setForm((p) => ({ ...p, deliveryItems: p.deliveryItems.filter((_, i) => i !== idx) }))}
              style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', flexShrink: 0 }}
            >
              삭제
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── 담당자 섹션 ───────────────────────────────────────────────────────────────
export function PersonnelSection({ form, errors, setField, inp, isMobile, users, salesPersons, isNew, cfg = null }) {
  const { vis, lab } = makeHelpers(cfg, 'personnel', isNew, ['sales_person_name']);
  return (
    <div style={sectionCard}>
      <div style={sectionTitleBar('#8b5cf6')}>
        <span style={sectionDot('#8b5cf6')} />
        담당자
      </div>
      <div style={twoColStyle}>
        {vis('user_name') && <FormField label={lab('user_name', '상담자')}>
          {isNew ? (
            <select style={inp('user_name')} value={form.user_name}
              onChange={(e) => setField('user_name', e.target.value)}>
              <option value="">선택하세요</option>
              {users.map((u) => (
                <option key={u.id} value={u.name}>{u.name}</option>
              ))}
            </select>
          ) : (
            <input style={{ ...inputStyle, fontSize: isMobile ? 9 : 11, background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}
              value={form.user_name} readOnly />
          )}
        </FormField>}
        {vis('sales_person_name') && <FormField label={lab('sales_person_name', '마케팅 담당자')}>
          <select
            style={inp('sales_person_name')}
            value={form.sales_person_name}
            onChange={(e) => setField('sales_person_name', e.target.value)}
          >
            <option value="">선택하세요</option>
            {salesPersons.map((sp) => (
              <option key={sp.id} value={sp.name}>{sp.name}</option>
            ))}
          </select>
        </FormField>}
      </div>
    </div>
  );
}
